import { cn } from '@/shared/utils/cn';
import type { AlertColor } from '@/shared/types/alert';
import { Alert } from '@/modules/Alert';
import type {
  NotificationAppearanceProps,
  NotificationIntent,
  NotificationLeadingProps,
  NotificationMetaProps,
  NotificationCardProps,
} from './types/notification';
import { NotificationContentBody } from './components/molecules/NotificationContentBody';

const INTENT_TO_ALERT_COLOR: Record<NotificationIntent, AlertColor> = {
  informativa: 'info',
  confirmacion: 'success',
  preventiva: 'warning',
  critica: 'danger',
  brand: 'brand',
  neutral: 'neutral',
};

const DEFAULT_LEADING: NotificationLeadingProps = {
  source: 'general',
  showIntentBadge: true,
};

const DEFAULT_APPEARANCE: NotificationAppearanceProps = {
  intent: 'informativa',
  size: 'sm',
  variant: 'outlined',
  animation: 'none',
};

const DEFAULT_META: NotificationMetaProps = {
  presentation: 'popup',
};

/**
 * Notificación de producto según Figma ([Notification](https://www.figma.com/design/aWDHIXKjf82m6udooBUeZG/Notification)): compone {@link Alert}
 * con API agrupada (`content`, `leading`, `appearance`, `meta`, `events`).
 *
 * @example Popup con avatar y badge de intención (Figma nodo Content usuario)
 * ```tsx
 * <Notification
 *   open
 *   content={{ title: 'Notification Title', subtitle: 'Subtitle', description: '…' }}
 *   leading={{
 *     source: 'usuario',
 *     imageUrl: 'https://…/avatar.jpg',
 *     showIntentBadge: true,
 *   }}
 *   meta={{ presentation: 'popup', stackSummary: '5 notificaciones más', duration: 8000 }}
 *   events={{ onClose: … }}
 * />
 * ```
 *
 * @example List-item panel (Figma 182:3679): punto «leída» clicable
 * ```tsx
 * <Notification
 *   open
 *   content={{ title: '…', subtitle: '…', description: '…' }}
 *   leading={{ source: 'general' }}
 *   meta={{ presentation: 'list-item', timestamp: '15:23', unread: true }}
 *   events={{ onMarkAsRead: () => setUnread(false) }}
 * />
 * ```
 */
export const Notification = ({
  open,
  content,
  leading: leadingProp,
  appearance: appearanceProp,
  meta: metaProp,
  events: eventsProp,
  footer,
}: NotificationCardProps) => {
  const leading = { ...DEFAULT_LEADING, ...leadingProp };
  const appearance = { ...DEFAULT_APPEARANCE, ...appearanceProp };
  const meta = { ...DEFAULT_META, ...metaProp };
  const events = eventsProp ?? {};

  if (process.env.NODE_ENV !== 'production') {
    if ((leading.source === 'usuario' || leading.source === 'sistema') && !leading.imageUrl) {
      // eslint-disable-next-line no-console
      console.warn(
        'Notification: se recomienda `leading.imageUrl` cuando `leading.source` es "usuario" o "sistema".'
      );
    }
  }

  const color = INTENT_TO_ALERT_COLOR[appearance.intent ?? 'informativa'];
  const showCloseButton = appearance.showCloseButton ?? meta.presentation === 'popup';

  const avatar = leading.source === 'general' ? undefined : leading.imageUrl;
  const hasLeadingImage = !!avatar;
  const showIntentBadge = hasLeadingImage && leading.showIntentBadge !== false;

  const isInlineRow = meta.presentation === 'item' || meta.presentation === 'list-item';

  const hasTimestamp =
    meta.timestamp !== null && meta.timestamp !== undefined && meta.timestamp !== '';

  const endAdornment =
    isInlineRow && (hasTimestamp || meta.unread) ? (
      <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-8 ui-self-start">
        {meta.timestamp ? (
          <span className="ui-text-body-xs-regular ui-font-body-xs-regular ui-tabular-nums ui-text-tertiary">
            {meta.timestamp}
          </span>
        ) : null}
        {meta.unread ? (
          meta.presentation === 'list-item' && events.onMarkAsRead ? (
            <button
              type="button"
              className={cn(
                'ui-group ui-inline-flex ui-shrink-0 ui-cursor-pointer ui-items-center ui-justify-center ui-rounded-control-pill ui-border-0 ui-bg-transparent ui-p-6',
                'ui-transition-transform ui-duration-150 ui-ease-out motion-reduce:ui-transition-none',
                'focus-visible:ui-outline-brand focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2'
              )}
              aria-label="Marcar como leída"
              onClick={(e) => {
                e.stopPropagation();
                events.onMarkAsRead?.();
              }}
            >
              <span
                className="ui-inline-block ui-h-12 ui-w-12 ui-rounded-control-pill ui-bg-fill-brand ui-transition-transform ui-duration-150 ui-ease-out group-hover:ui-scale-110 group-active:ui-scale-95 motion-reduce:ui-transition-none"
                aria-hidden
              />
            </button>
          ) : (
            <span
              className="ui-inline-block ui-h-12 ui-w-12 ui-shrink-0 ui-rounded-control-pill ui-bg-fill-brand"
              aria-label="Notificación no leída"
            />
          )
        ) : null}
      </div>
    ) : undefined;

  const surfaceClass = cn(
    'ui-rounded-surface-card ui-bg-fill ui-min-w-0',
    /* Tarjeta popup [Figma Notification](https://www.figma.com/design/aWDHIXKjf82m6udooBUeZG/Notification?node-id=242-6622&m=dev): borde sutil + sombra 05 (el Alert aporta ancho de borde). */
    meta.presentation === 'popup' && 'ui-border-subtle ui-shadow-05',
    appearance.className,
    /* Sin borde ni sombra de Alert (variant outlined/contained): debe ir al final para ganar a `appearance.className` y al preset del Alert. */
    meta.presentation === 'list-item' &&
      '!ui-border-0 ui-border-transparent ui-shadow-none !ui-shadow-none !ui-border-l-0 ui-ring-0',
    /* Mismo ancho siempre (Figma 372px). Al final sustituye el `ui-w-full` del Alert; `max-w-full` recorta en contenedores más estrechos que 372px. */
    'ui-w-[372px] ui-max-w-full ui-shrink-0'
  );

  const effectiveDuration: number | undefined =
    meta.presentation === 'popup' ? meta.duration : undefined;

  const contentKind = content.kind ?? 'default';
  const useEnrichedBody = contentKind !== 'default';
  const isCtaKind = contentKind === 'cta';

  const headerPrimaryLabel = events.primaryButton?.label ?? events.primaryButtonText;
  const headerSecondaryLabel = events.secondaryButton?.label ?? events.secondaryButtonText;
  const headerOnPrimary = events.primaryButton?.onClick ?? events.onPrimaryClick;
  const headerOnSecondary = events.secondaryButton?.onClick ?? events.onSecondaryClick;

  const enrichedBody = useEnrichedBody ? (
    <NotificationContentBody content={content} events={events} />
  ) : null;

  return (
    <Alert
      open={open}
      title={content.title}
      message={content.subtitle}
      description={useEnrichedBody ? undefined : content.description}
      color={color}
      variant={appearance.variant}
      size={appearance.size}
      animation={appearance.animation}
      truncateHeaderText
      showCloseButton={showCloseButton}
      onClose={events.onClose}
      onClick={events.onClick}
      onPrimaryClick={isCtaKind ? undefined : headerOnPrimary}
      onSecondaryClick={isCtaKind ? undefined : headerOnSecondary}
      primaryButtonText={isCtaKind ? undefined : headerPrimaryLabel}
      secondaryButtonText={isCtaKind ? undefined : headerSecondaryLabel}
      avatar={avatar}
      avatarIntentBadge={showIntentBadge}
      duration={effectiveDuration}
      progressBarClassName={meta.progressBarClassName}
      endAdornment={endAdornment}
      headerAddon={enrichedBody}
      className={surfaceClass}
      isLeaving={appearance.isLeaving}
    >
      {meta.stackSummary && meta.presentation === 'popup' ? (
        <div className="ui-flex ui-w-full ui-min-w-0 ui-max-w-full ui-justify-center">
          <span
            className="ui-block ui-min-w-0 ui-max-w-full ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-center ui-text-body-xs-regular ui-font-body-xs-regular ui-text-tertiary"
            title={meta.stackSummary}
          >
            {meta.stackSummary}
          </span>
        </div>
      ) : null}
      {footer}
    </Alert>
  );
};

Notification.displayName = 'Notification';

export type {
  NotificationIntent,
  NotificationSource,
  NotificationPresentation,
  NotificationCardProps,
  NotificationContentProps,
  NotificationContentKind,
  NotificationContentFileProps,
  NotificationContentLinkProps,
  NotificationContentCtaProps,
  NotificationCtaButtonAction,
  NotificationHeaderButtonAction,
  NotificationLeadingProps,
  NotificationAppearanceProps,
  NotificationMetaProps,
  NotificationEventsProps,
} from './types/notification';
